import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { css } from "@emotion/css";
import EventPageMainContainer from "../../features/events/components/event-page-main-container/event-page-main-container";
import EventDescription from "../../features/events/components/event-description/event-description";
import EventTicketBuyer from "../../features/events/components/event-ticket-buyer/event-ticket-buyer";
import EventPerformerCard from "../../features/events/components/event-performer-card/event-performer-card";
import EventLocationCard from "../../features/events/components/event-location-card/event-location-card";
import EventMapCard from "../../features/events/components/event-map-card/event-map-card";
import EventCarousel from "../../features/events/components/event-carousel/event-carousel";
import { useGetEventById, useGetEvents } from "../../features/events/hooks/events-api.hooks";
import { Navbar } from "../../shared/components/navbar/navbar";
import { Footer } from "../../shared/components/footer/footer";
import PaymentSecurityFooter from "../../shared/components/payment-security-footer/payment-security-footer";
import { IoCalendarOutline, IoLocationOutline } from "react-icons/io5";
import { SectionTitle } from "../../shared/ui/section-title/section-title";
import { useLoading } from "../../features/loading";

const container = css({
  width: "clamp(0rem, 100%, 90rem)",
  margin: "0 auto",
  padding: "0 1.6rem",
});

const pageWrapper = css({
  position: "relative",
});

const contentWrapper = css({
  paddingRight: "calc(30rem + var(--spacing-xl))",
  
  "@media (max-width: 62rem)": {
    paddingRight: 0,
  },
});

const leftColumn = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-xl)",
});

const mobileTicketBuyer = css({
  display: 'none',
  
  "@media (max-width: 62rem)": {
    display: "block",
    paddingTop: "var(--spacing-xl)",
  },
});

const relatedEventsSection = css({
  width: "100%",
  margin: "var(--spacing-3xl) 0",
  overflow: "hidden",
});

const relatedEventsHeader = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-sm)",
  marginBottom: "var(--spacing-lg)",
  paddingLeft: "var(--spacing-sm)",
});

const sectionIcon = css({
  color: "var(--pc-700)",
  minWidth: "20px",
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const absoluteColumn = css({
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  width: "30rem",
  zIndex: 10,
  
  "@media (max-width: 62rem)": {
    display: "none",
  },
});

const ticketBuyerSticky = css({
  position: "sticky",
  top: "6rem",
  height: "fit-content",
  marginTop: "24rem",
  
  "@media (max-width: 62rem)": {
    position: "static",
    marginTop: 0,
  },
});

const bannerLeft = css({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const bannerRight = css({
  width: "30rem",
  flexShrink: 0,
  position: "relative",

  "@media (max-width: 62rem)": {
    display: "none",
  },
});

const titleContainer = css({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
});

const eventTitle = css({
  fontSize: "2.5rem",
  fontWeight: "700",
  margin: 0,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  
  "@media (max-width: 768px)": {
    fontSize: "2rem",
  },
  
  "@media (max-width: 480px)": {
    fontSize: "1.75rem",
  },
});

const infoRow = css({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "1rem",
  opacity: 0.95,
  
  "@media (max-width: 768px)": {
    fontSize: "0.9375rem",
  },
  
  "@media (max-width: 480px)": {
    fontSize: "0.875rem",
  },
});

const formatEventDate = (schedules: Array<{ date: string }>): string => {
  if (!schedules || schedules.length === 0) return "";

  // Normalize incoming date strings and work in UTC-only to avoid off-by-one day due to timezones
  const extractYMD = (s: string): string => (s.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? s.split('T')[0] ?? s);
  const toUtcDateOnly = (s: string): Date => new Date(`${extractYMD(s)}T00:00:00Z`);

  const dates = schedules
    .map(s => toUtcDateOnly(s.date))
    .sort((a, b) => a.getTime() - b.getTime());

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  // Compare using UTC components
  const sameDay = firstDate.getUTCDate() === lastDate.getUTCDate() &&
                  firstDate.getUTCMonth() === lastDate.getUTCMonth() &&
                  firstDate.getUTCFullYear() === lastDate.getUTCFullYear();

  if (sameDay) {
    return firstDate.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  }

  const sameMonth = firstDate.getUTCMonth() === lastDate.getUTCMonth() &&
                    firstDate.getUTCFullYear() === lastDate.getUTCFullYear();

  if (sameMonth) {
    const firstDay = String(firstDate.getUTCDate());
    const lastDay = String(lastDate.getUTCDate());
    const monthYear = firstDate.toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" });
    return `${firstDay} a ${lastDay} de ${monthYear}`;
  }

  // Different months/years
  const firstLabel = firstDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", timeZone: "UTC" });
  const lastLabel = lastDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
  return `${firstLabel} a ${lastLabel}`;
};

const getUniqueLocations = (schedules: Array<{ location: { name: string } }>): string => {
  const locations = [...new Set(schedules.map(s => s.location.name))];
  return locations.join(", ");
};

const getTheaterDescription = (theaterName: string): string => {
  switch (theaterName) {
    case "Teatro Gamaro":
      return "Teatro Gamaro é um teatro histórico de 744 lugares na Mooca, São Paulo, instalado em uma antiga fábrica da Alpargatas. Inaugurado em 2009, o espaço de 2.000m² combina patrimônio industrial com infraestrutura moderna, recebendo desde peças teatrais até shows e eventos corporativos.";
    default:
      return "Este teatro é conhecido por sua excelente acústica e conforto. Com uma história rica na cena cultural, oferece uma experiência única para os espectadores, combinando tradição e modernidade em suas instalações.";
  }
};

export const Event: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: eventData, isLoading: isLoadingEvent, isError } = useGetEventById(id || "");
  const { data: allEventsData } = useGetEvents({ limit: 10 });
  const { setLoading, isLoading } = useLoading();

  useEffect(() => {
    setLoading(isLoadingEvent);
  }, [isLoadingEvent, setLoading]);

  // Don't render content while loading
  if (isLoading || isLoadingEvent) {
    return null;
  }

  // Only show error after loading is complete
  if (isError || !eventData?.event) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        Erro ao carregar evento
      </div>
    );
  }

  const event = eventData.event;

  return (
    <div>
      <Navbar />
      <div className={container}>
        <div className={pageWrapper}>
          <div className={absoluteColumn}>
            <EventTicketBuyer 
              event={event}
              className={ticketBuyerSticky}
            />
          </div>
          
          <EventPageMainContainer bannerImageData={event.bannerImage}>
            <div className={bannerLeft}>
              <div className={titleContainer}>
                <h1 className={eventTitle}>{event.title}</h1>
              </div>
              
              {event.schedules && event.schedules.length > 0 && (
                <div className={infoRow}>
                  <IoCalendarOutline size={20} />
                  <span>{formatEventDate(event.schedules)}</span>
                </div>
              )}
              
              {event.schedules && event.schedules.length > 0 && (
                <div className={infoRow}>
                  <IoLocationOutline size={20} />
                  <span>{getUniqueLocations(event.schedules)}</span>
                </div>
              )}
            </div>
            
            <div className={bannerRight} />
          </EventPageMainContainer>
          
          <div className={contentWrapper}>
            <div className={leftColumn}>
              <EventDescription event={event} />
              
              {event.performers && event.performers.length > 0 && (
                <EventPerformerCard performer={event.performers[0]} />
              )}
              
              {event.schedules && event.schedules.length > 0 && (
                <>
                  <EventLocationCard
                    theaterName={event.schedules[0].location.name}
                    theaterDescription={getTheaterDescription(event.schedules[0].location.name)}
                    theaterImage={event.schedules[0].location.image}
                  />
                  
                  <EventMapCard
                    theaterName={event.schedules[0].location.name}
                    theaterAddress={`${event.schedules[0].location.name}, São Paulo - SP`}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Ticket Buyer on Mobile - appears before related events */}
        <div className={mobileTicketBuyer}>
          <EventTicketBuyer event={event} />
        </div>
        
        {/* Related Events Carousel */}
        {allEventsData && allEventsData.events && allEventsData.events.length > 0 && (
          <div className={relatedEventsSection}>
            <div className={relatedEventsHeader}>
              <IoCalendarOutline className={sectionIcon} />
              <SectionTitle level={2}>Eventos Relacionados</SectionTitle>
            </div>
            <EventCarousel 
              events={allEventsData.events.filter(e => e.id !== id).slice(0, 10)} 
            />
          </div>
        )}
        
        {/* Payment Security Footer */}
        <PaymentSecurityFooter />
      </div>
      <Footer />
    </div>
  );
};

export default Event;
